import { useCallback, useEffect, useRef } from 'react'

import type {
  ColumnsResetEvent,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-community'

import { Breakpoints } from '@b-stock/fe-core/design-system'

import { useIsMobileTableLayout } from '@/components/listings/shared/useIsMobileTableLayout'

export function useColumnPinning({
  alwaysPin = false,
  colId,
  gridApi,
  pinDirection,
}: {
  /**
   * Keeps the column pinned below the mobile breakpoint too, instead of letting
   * it scroll away with the rest.
   */
  alwaysPin?: boolean
  colId: string
  gridApi: GridApi | null
  pinDirection: 'left' | 'right'
}) {
  const isMobile = useIsMobileTableLayout()

  /**
   * isMobileAtMountRef is used instead of useIsMobileTableLayout to avoid
   * the pinned column "sliding in" on desktop due to its initial mobile default.
   *
   * useIsMobileTableLayout returns true on first render before its effect runs.
   *
   * Pass isMobileAtMountRef.current to column defs for the initial pin state.
   * A ref keeps the value stable across viewport changes while mounted, so
   * column def memos don't recompute and col def identity is preserved.
   */
  const isMobileAtMountRef = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia(Breakpoints.max.medium).matches
      : true
  )

  /**
   * Watch for changes to the viewport size and update column pin state.
   *
   * Pinning is controlled via the API rather than column definitions so that
   * column def identity is stable. Changing col defs causes AG Grid to reset
   * all column state (widths, order, visibility), discarding user preferences.
   */
  useEffect(() => {
    if (!gridApi) return
    gridApi.setColumnsPinned(
      [colId],
      alwaysPin || !isMobile ? pinDirection : null
    )
  }, [alwaysPin, colId, gridApi, isMobile, pinDirection])

  /**
   * Prevent a "flash" of the wrong state caused by agGridMemory, if resize
   * happens between renders. Call after agGridMemoryProps.onGridReady, which
   * may restore a pinned state from a different viewport width.
   */
  const onGridReadyAfterMemory = useCallback(
    (event: GridReadyEvent) => {
      event.api.setColumnsPinned(
        [colId],
        alwaysPin || !isMobileAtMountRef.current ? pinDirection : null
      )
    },
    [alwaysPin, colId, pinDirection]
  )

  /**
   * "Reset Columns" rebuilds column state from the definitions, and the pinned
   * value there is frozen at mount. Reset on a phone after a desktop mount
   * would otherwise restore the desktop pin, and the effect above cannot undo
   * it — the viewport has not changed, so it does not re-run.
   */
  const onColumnsReset = useCallback(
    (event: ColumnsResetEvent) => {
      event.api.setColumnsPinned(
        [colId],
        alwaysPin || !isMobile ? pinDirection : null
      )
    },
    [alwaysPin, colId, isMobile, pinDirection]
  )

  return { isMobileAtMountRef, onColumnsReset, onGridReadyAfterMemory }
}
